import PropTypes from 'prop-types';
import { useState } from 'react';
import moment from 'moment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
} from '@mui/material';
import Iconify from 'src/components/Iconify';

// -----------------------------------------------

function defaultResumeAt() {
    const tomorrow = moment().add(1, 'day').startOf('day').add(9, 'hours');
    return tomorrow;
}

// -----------------------------------------------

export default function PauseTaskDialog({ open, onClose, onConfirm, loading }) {
    const [resumeAt, setResumeAt] = useState(defaultResumeAt);

    const handleConfirm = () => {
        if (!resumeAt || !resumeAt.isValid()) return;
        onConfirm(resumeAt.toISOString());
    };

    const isPast = resumeAt && resumeAt.isValid() && resumeAt.isBefore(moment());

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
            <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Iconify icon="eva:pause-circle-outline" width={22} sx={{ color: 'warning.main' }} />
                Pause this task?
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    We&apos;ll create a reminder and auto-resume the task at this time.
                </Typography>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DateTimePicker
                        label="Resume on"
                        value={resumeAt}
                        onChange={(v) => setResumeAt(v)}
                        minDateTime={moment().add(1, 'minute')}
                        slotProps={{
                            textField: {
                                size: 'small',
                                fullWidth: true,
                                error: Boolean(isPast),
                                helperText: isPast ? 'Resume time must be in the future' : '',
                            },
                        }}
                    />
                </LocalizationProvider>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
                <Stack direction="row" spacing={1} sx={{ width: '100%' }} justifyContent="flex-end">
                    <Button variant="outlined" color="inherit" onClick={onClose} disabled={loading} sx={{ textTransform: 'none', borderRadius: '8px' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={handleConfirm}
                        disabled={loading || !resumeAt?.isValid() || isPast}
                        startIcon={<Iconify icon="eva:pause-circle-fill" width={16} />}
                        sx={{ textTransform: 'none', borderRadius: '8px' }}
                    >
                        {loading ? 'Pausing…' : 'Pause task'}
                    </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
}

PauseTaskDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
    loading: PropTypes.bool,
};

PauseTaskDialog.defaultProps = {
    loading: false,
};
